<template>
  <span class="stats-trend" :class="trendClass">
    <BaseIcon :name="trendIcon" size="10" />
    <span class="stats-trend__value">{{ formattedValue }}</span>
  </span>
</template>

<script>
  /**
   * StatsTrend Component
   *
   * Displays a trend indicator showing percentage change with directional icon.
   * Positive values show green with up arrow, negative values show red with down arrow.
   *
   * @example
   * <StatsTrend :value="12.5" />           // Shows: ↑ +12.5%
   * <StatsTrend :value="-5.2" />           // Shows: ↓ -5.2%
   * <StatsTrend :value="8" suffix="pts" /> // Shows: ↑ +8 pts
   */
  import { defineComponent, computed } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  export default defineComponent({
    name: 'StatsTrend',

    components: {
      BaseIcon,
    },

    props: {
      /**
       * The trend value (positive or negative number)
       */
      value: {
        type: Number,
        required: true,
      },
      /**
       * Suffix to display after the value (default: '%')
       */
      suffix: {
        type: String,
        default: '%',
      },
      /**
       * Invert colors (positive = bad, negative = good)
       */
      inverted: {
        type: Boolean,
        default: false,
      },
    },

    setup(props) {
      const isPositive = computed(() => props.value > 0);
      const isNegative = computed(() => props.value < 0);
      const isNeutral = computed(() => props.value === 0);

      const trendClass = computed(() => {
        if (isNeutral.value) return 'stats-trend--neutral';
        if (props.inverted) {
          return isPositive.value ? 'stats-trend--negative' : 'stats-trend--positive';
        }
        return isPositive.value ? 'stats-trend--positive' : 'stats-trend--negative';
      });

      const trendIcon = computed(() => {
        if (isNeutral.value) return 'minus';
        return isPositive.value ? 'arrow-up-right' : 'arrow-down-right';
      });

      const formattedValue = computed(() => {
        const absValue = Math.abs(props.value);
        const sign = isPositive.value ? '+' : isNegative.value ? '-' : '';
        // Format to max 1 decimal place
        const formatted = absValue % 1 === 0 ? absValue.toString() : absValue.toFixed(1);
        return `${sign}${formatted}${props.suffix}`;
      });

      return {
        trendClass,
        trendIcon,
        formattedValue,
      };
    },
  });
</script>

<style scoped lang="scss">
  .stats-trend {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 8px;
    border-radius: 4px;
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1;

    &__value {
      white-space: nowrap;
    }

    &--positive {
      background-color: var(--green-50);
      color: var(--green-700);

      .icon {
        color: var(--green-600);
      }
    }

    &--negative {
      background-color: var(--red-50);
      color: var(--red-700);

      .icon {
        color: var(--red-600);
      }
    }

    &--neutral {
      background-color: var(--slate-100);
      color: var(--slate-500);

      .icon {
        color: var(--slate-400);
      }
    }
  }
</style>
